<template>
  <div class="relative flex-1 flex flex-col">
    <!-- Photo Green Border Flash -->
    <div v-if="photoFlash" class="fixed inset-0 z-50 pointer-events-none border-[6px] border-green-500 rounded-lg" style="animation: borderFlash 0.6s ease-out forwards;"></div>
    <!-- Loading Overlay -->
    <div v-if="loading" class="flex justify-center items-center h-screen">
      <p class="text-lg font-medium text-neutral-900">Загрузка...</p>
    </div>
    <template v-if="taskData">
      <!-- Специфичные экраны для типов операций -->
      <PackingPlaces
        v-if="activeView === 'packing'"
        :task-id="taskData.production_task_id"
        @close="activeView = 'default'; $nextTick(() => { if ($refs.packingPlacesSummary) $refs.packingPlacesSummary.loadData() })"
      />
      <main v-else class="flex-1 px-6 py-2 bg-neutral-50">
        <div class="flex h-[85vh]">
          <!-- Левый блок -->
          <div class="w-30 pr-6 flex flex-col gap-4 h-full">
            <!-- Верхний подблок. Карточка задания ужимается до половины колонки и скроллит
                 собственное содержимое: иначе она занимает высоту контента целиком,
                 а блоку очереди под ней не остаётся ничего, кроме внутренних отступов -->
            <div class=" bg-white rounded-2xl shadow py-2 px-4 flex flex-col min-h-0 overflow-auto max-h-[50%]">
              <h2 class="text-lg font-semibold m-3">
                <span :class="{ 'line-through text-gray-500': taskData?.product_deleted_at }">{{ taskData?.product_name }}</span>
                <span v-if="taskData?.product_deleted_at" class="ml-1 text-xs text-red-600 font-medium">Удалена</span>
              </h2>
              <div class="flex flex-row m-1.5 gap-5">
                <div class="w-3/5 flex flex-col gap-6 px-3">
                  <img
                    v-if="taskData?.img_url"
                    class="max-h-32 object-contain rounded-md mb-2 cursor-zoom-in"
                    :src="productImageSrc"
                    alt="Изображение"
                    title="Открыть на весь экран"
                    @click="fullscreenImageSrc = productImageSrc"
                  />
                </div>
              </div>
              <div class="text-sm text-neutral-700 space-y-1 m-3">
                <p>Заказ: <span class="text-neutral-900 font-medium">#{{ taskData?.production_order_id }}</span></p>
                <p>Дата заказа: <span class="text-neutral-900 font-medium">{{ formatDate(taskData?.order_date) }}</span></p>
                <p>Спецификация: <span class="text-neutral-900 font-medium">{{ taskData?.specification }}</span></p>
                <p v-if="taskData?.commercial_order_number">Коммерческий заказ: <span class="text-neutral-900 font-medium">{{ taskData?.commercial_order_number }}</span></p>
                <p>Задание: <span class="text-neutral-900 font-medium">#{{ taskData?.task_number }}</span></p>
                <p v-if="taskData?.batch_product_id" class="flex items-center gap-2 flex-wrap">
                  Серийный номер:
                  <template v-if="!isSerialEditing">
                    <span class="text-neutral-900 font-medium">{{ taskData?.serial_number || '—' }}</span>
                    <button
                      v-can-write
                      type="button"
                      @click="startSerialEdit"
                      class="py-0.5 -ml-1 text-sm text-blue-600 hover:text-blue-800 underline"
                    >
                      Изменить
                    </button>
                  </template>
                  <template v-else>
                    <input
                      ref="serialInput"
                      v-model="serialDraft"
                      type="text"
                      class="px-2 py-1 border border-gray-300 rounded text-neutral-900 font-medium text-sm min-w-[10rem] focus:ring-2 focus:ring-blue-400 focus:border-blue-400 outline-none disabled:bg-gray-100"
                      :disabled="serialSaving"
                      @keyup.enter="saveSerial"
                      @keyup.esc="cancelSerialEdit"
                    />
                    <button
                      type="button"
                      @click="saveSerial"
                      :disabled="serialSaving"
                      class="py-0.5 text-sm text-blue-600 hover:text-blue-800 underline disabled:opacity-50"
                    >
                      {{ serialSaving ? 'Сохранение...' : 'Сохранить' }}
                    </button>
                    <button
                      type="button"
                      @click="cancelSerialEdit"
                      :disabled="serialSaving"
                      class="py-0.5 text-sm text-neutral-500 hover:text-neutral-700 underline disabled:opacity-50"
                    >
                      Отмена
                    </button>
                  </template>
                </p>
                <p>Изготовлено: <span class="text-neutral-900 font-medium"> {{ taskData?.completed_count }} из {{ taskData?.total_count }}</span></p>
                <div class="w-full h-2 bg-neutral-200 rounded overflow-hidden mt-1">
                  <div class="h-full bg-blue-500" :style="{ width: `${(taskData?.completed_count / taskData?.total_count) * 100}%` }"></div>
                </div>
              </div>
            </div>
            <!-- Нижний подблок -->
            <div class="flex-1 min-h-0 bg-white rounded-2xl shadow p-4 overflow-auto">
              <h3 class="font-semibold text-2xl m-2">Очередь:</h3>
              <ul class="text-sm text-neutral-800 mx-2 mt-4">
                <li v-for="(item, index) in taskData?.queue" :key="index" @click="selectQueueItem(item)" class="flex flex-col my-2 gap-1 cursor-pointer hover:bg-gray-50 rounded px-1">
                  <span class="text-lg text-gray-600">Производственный заказ #{{ item.production_order_id }}</span>
                  <span v-if="item.commercial_order_number" class="text-sm text-gray-500">Коммерческий: {{ item.commercial_order_number }}</span>
                  <span v-if="item.operation_id === taskData?.id" class="inline-block w-fit text-xs px-2 py-0.5 rounded bg-blue-100 text-blue-700 font-medium">
                    Текущий
                  </span>
                  <span v-else-if="item.is_busy" class="inline-block w-fit text-xs px-2 py-0.5 rounded bg-red-100 text-red-700 font-medium">
                    В работе{{ item.owner_name ? ' — ' + item.owner_name : '' }}
                  </span>
                  <div class="flex justify-between">
                    <span class="text-base font-semibold">
                      <span :class="{ 'line-through text-gray-500': item.product_deleted_at }">{{ item.product_name }}</span>
                      <span v-if="item.product_deleted_at" class="ml-1 text-xs text-red-600 font-medium">Удалена</span>
                    </span>
                    <span class="font-semibold">{{ formatQuantity(item.quantity) }} {{ getUnitLabel(item) }}</span>
                  </div>
                  <hr class="my-1"/>
                </li>
              </ul>
            </div>
          </div>
          <!-- Центральная и правая часть -->
          <div class="flex-1 flex flex-col h-full gap-4">
            <!-- Операция -->
            <div>
              <h2 class="text-sm font-normal text-neutral-500">Операция</h2>
              <h3 class="text-3xl font-semibold text-neutral-900">{{ taskData?.operation_name }}</h3>
            </div>
            <!-- Общий блок с белым фоном -->
            <div class="flex-1 bg-white rounded-2xl shadow p-4 flex gap-6 h-2/3">
              <!-- Второй блок -->
              <div class="flex-1 flex flex-col overflow-auto">
                <div class="mb-2 flex flex-row gap-2">
                  <div class="basis-2/3">
                    <div class="text-neutral-500 mb-1.5">Текущий шаг:</div>
                    <div class="text-neutral-900 font-semibold text-lg">
                      {{ currentStep?.operation_step?.name }}
                    </div>
                  </div>
                  <div class="basis-1/3">
                    <div class="text-neutral-500 mb-1.5">Номер шага:</div>
                    <div class="text-neutral-900 font-semibold text-lg">{{ currentStepIndex + 1 }}/{{ taskData?.steps.length }}
                    </div>
                  </div>
                </div>
                <hr class="mt-3 mb-3"/>
                <div>
                  <h4 class="text-neutral-900 font-semibold text-lg mb-3">Описание шага</h4>
                  <p class="text-base text-neutral-600">{{ currentStep?.operation_step?.description }}</p>
                </div>
                <!-- Прикреплённые документы шага -->
                <div v-if="isOperationStarted" class="mt-3 mb-3 self-start">
                  <input
                    ref="stepDocumentInput"
                    type="file"
                    class="hidden"
                    @change="handleStepDocumentSelect"
                  />
                  <button
                    v-can-write
                    @click="triggerStepDocumentUpload"
                    :disabled="isUploadingDocument"
                    class="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed text-base font-medium inline-flex items-center gap-2"
                  >
                    <i class="fas" :class="isUploadingDocument ? 'fa-spinner fa-spin' : 'fa-paperclip'"></i>
                    {{ isUploadingDocument ? 'Загрузка...' : 'Добавить документ' }}
                  </button>
                  <ul v-if="currentStepDocuments.length > 0" class="mt-2 space-y-1">
                    <li
                      v-for="doc in currentStepDocuments"
                      :key="doc.file_id"
                      class="flex items-center justify-between text-base text-neutral-700 bg-neutral-50 rounded px-3 py-1.5"
                    >
                      <a :href="doc.preview_url" target="_blank" class="text-blue-600 hover:underline truncate">
                        {{ doc.name }}
                      </a>
                    </li>
                  </ul>
                </div>
                <template v-if="currentStep?.operation_step?.type === 'print' && currentStep.operation_step.documents.length > 0">
                  <button
                    @click="printDocument"
                    :disabled="!isOperationStarted || isPrinting"
                    class="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                    title="Начните операцию, чтобы печатать"
                  >
                    <i class="fas" :class="isPrinting ? 'fa-spinner fa-spin' : 'fa-print'"></i>
                    Распечатать документ
                  </button>
                </template>
                <template v-if="isPackagingStep && isOperationStarted">
                  <button
                    @click="goToPackingPlaces"
                    class="px-4 py-2 bg-indigo-600 text-white rounded hover:bg-indigo-700 text-lg font-semibold"
                  >
                    Упаковочные места
                  </button>
                  <PackingPlacesSummary
                    ref="packingPlacesSummary"
                    :task-id="taskData.production_task_id"
                  />
                </template>
                <template v-if="currentStep?.operation_step?.components.length > 0">
                  <hr class="mt-3 mb-3"/>
                  <!-- Шаг «Используемое ПО»: только просмотр ключей активации.
                       Стоит выше комплектования, иначе на столе комплектовщика ПО
                       попало бы в блок взятия комплектующих -->
                  <div v-if="isSoftwareStep">
                    <h4 class="text-neutral-900 font-semibold text-lg mb-3">Используемое ПО:</h4>
                    <table class="w-full text-base text-left">
                      <thead class="text-neutral-500">
                        <tr>
                          <th class="py-1 pr-4 font-medium">ПО</th>
                          <th class="py-1 font-medium">Ключ активации</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr
                          v-for="software in currentStepSoftwareKeys"
                          :key="'software-' + software.product_id"
                          class="border-t border-neutral-200"
                        >
                          <td class="py-2 pr-4 text-neutral-700">{{ software.name }}</td>
                          <td class="py-2">
                            <span v-if="software.activation_key" class="font-semibold text-neutral-900 break-all">{{ software.activation_key }}</span>
                            <span v-else class="text-red-600">Ключ не указан</span>
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                  <!-- Интеллектуальное комплектование для операций комплектования -->
                  <div v-else-if="isPickingBlockVisible">
                    <PickingComponents
                      ref="pickingComponents"
                      :operation-id="productionTaskOperationId"
                      :workshopPostUnitId="parseInt($route.params.id)"
                      :components="currentStep.operation_step.components"
                      :saved-progress="pickedItemsData"
                      @picked-items-data="handlePickedItemsData"
                      @has-pending="handleHasPending"
                    />
                  </div>
                  <!-- Простой список для других операций -->
                  <div v-else>
                    <h4 class="text-neutral-900 font-semibold text-lg mb-3">Комплектование:</h4>
                    <ul class="list-none space-y-1 text-base text-neutral-600">
                      <li v-for="(component, index) in currentStep?.operation_step?.components" :key="index">
                        <span :class="{ 'line-through text-gray-500': component.product?.deleted_at }">{{ component.product?.name }}</span>
                        <span v-if="component.product?.deleted_at" class="ml-1 text-xs text-red-600 font-medium">Удалена</span>
                        — {{ formatQuantity(component.count) }} {{ getUnitLabel(component.product) }}
                      </li>
                    </ul>
                  </div>
                </template>
              </div>
              <!-- Третий блок: карусель изображений шага 16:9 -->
              <div v-if="stepImages.length > 0" class="flex-1 flex flex-col">
                <div class="aspect-video relative border rounded-md overflow-hidden bg-neutral-100 w-full">
                  <img
                    :src="currentStepImageSrc"
                    alt="Изображение шага"
                    class="absolute inset-0 w-full h-full object-contain cursor-zoom-in"
                    title="Открыть на весь экран"
                    @click="fullscreenImageSrc = currentStepImageSrc"
                  />
                  <button
                    v-if="stepImages.length > 1"
                    @click="stepImageCarouselIndex = stepImageCarouselIndex <= 0 ? stepImages.length - 1 : stepImageCarouselIndex - 1"
                    class="absolute left-2 top-1/2 -translate-y-1/2 w-8 h-8 rounded-full bg-white/90 shadow flex items-center justify-center hover:bg-white text-neutral-700"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 19l-7-7 7-7" />
                    </svg>
                  </button>
                  <button
                    v-if="stepImages.length > 1"
                    @click="stepImageCarouselIndex = stepImageCarouselIndex >= stepImages.length - 1 ? 0 : stepImageCarouselIndex + 1"
                    class="absolute right-2 top-1/2 -translate-y-1/2 w-8 h-8 rounded-full bg-white/90 shadow flex items-center justify-center hover:bg-white text-neutral-700"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7" />
                    </svg>
                  </button>
                  <span
                    class="absolute bottom-2 left-2 px-2 py-1 rounded bg-white/95 text-sm text-neutral-700 font-medium shadow"
                  >
                    {{ stepImageCarouselIndex + 1 }}/{{ stepImages.length }}
                  </span>
                </div>
              </div>
            </div>
            <!-- Кнопки -->
            <div class="h-24 py-3 flex gap-4">
              <button
                v-if="isOperationStarted"
                @click="openCameraPreview"
                :disabled="!cameraReady || isPhotoTaking"
                :class="[photoFlash ? 'bg-green-500 hover:bg-green-600' : 'bg-amber-500 hover:bg-amber-600']"
                class="shrink-0 w-64 text-white text-2xl font-semibold rounded-2xl transition-colors duration-300 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-3 relative"
              >
                <CameraIcon class="w-7 h-7" />
                Фото
                <span class="text-base opacity-75">({{ hotkeyLabel }})</span>
                <span v-if="photoCount > 0" class="absolute -top-2 -right-2 bg-green-500 text-white text-sm rounded-full w-7 h-7 flex items-center justify-center font-bold">{{ photoCount }}</span>
              </button>
              <button
                v-if="isOperationStarted"
                @click="openKeybindModal"
                class="shrink-0 w-14 bg-neutral-200 text-neutral-600 text-xl rounded-2xl hover:bg-neutral-300 transition flex items-center justify-center"
                title="Настроить клавишу для фото"
              >
                <Cog6ToothIcon class="w-6 h-6" />
              </button>
              <button
                v-if="isOperationStarted"
                @click="showPhotoGallery = !showPhotoGallery"
                :disabled="photoCount === 0"
                :class="[showPhotoGallery ? 'bg-blue-600 text-white' : 'bg-neutral-200 text-neutral-600']"
                class="shrink-0 w-14 text-xl rounded-2xl hover:opacity-80 transition flex items-center justify-center disabled:opacity-50 disabled:cursor-not-allowed"
                title="Галерея фото"
              >
                <PhotoIcon class="w-6 h-6" />
              </button>
              <button
                v-if="isOperationStarted && currentStepIndex > 0"
                @click="goToPreviousStep"
                class="w-full bg-blue-100 text-blue-700 text-3xl font-semibold py-4 rounded-2xl hover:bg-blue-200 transition"
              >
                ← Вернуться назад
              </button>
              <button
                v-if="!isOperationStarted && !hasSavedProgress && taskData?.steps?.length > 0"
                v-can-write
                @click="startOperationModal"
                class="w-full bg-blue-600 text-white text-3xl font-semibold py-4 rounded-2xl hover:bg-blue-700 transition"
              >
                Начать операцию
              </button>
              <button
                v-if="!isOperationStarted && hasSavedProgress && taskData?.steps?.length > 0"
                v-can-write
                @click="continueOperation"
                class="w-full bg-amber-500 text-white text-3xl font-semibold py-4 rounded-2xl hover:bg-amber-600 transition"
              >
                Продолжить операцию
              </button>
              <button
                v-if="isPickingBlockVisible && hasPendingPickingItems"
                v-can-write
                @click="savePickingProgress"
                class="w-full bg-orange-500 text-white text-3xl font-semibold py-4 rounded-2xl hover:bg-orange-600 transition disabled:opacity-50 disabled:cursor-not-allowed"
                :disabled="isSavingProgress"
              >
                {{ isSavingProgress ? 'Сохранение...' : 'Сохранить' }}
              </button>
              <button
                v-if="isOperationStarted && currentStepIndex < taskData?.steps.length - 1"
                @click="nextOperationModal"
                class="w-full bg-blue-600 text-white text-3xl font-semibold py-4 rounded-2xl hover:bg-blue-700 transition disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Следующий шаг →
              </button>
              <button
                v-if="(isOperationStarted && currentStepIndex === taskData?.steps.length - 1) || !taskData?.steps?.length"
                v-can-write
                @click="endOperationModal"
                :disabled="isSavingProgress"
                class="w-full bg-green-600 text-white text-3xl font-semibold py-4 rounded-2xl hover:bg-green-700 transition disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Завершить
              </button>
              <!-- Единая кнопка фиксации несоответствия на всех столах, а не только
                   на посту ОТК (Z-06): несоответствие обнаруживают и на комплектовании,
                   и на сборке, и на упаковке -->
              <button
                v-if="isOperationStarted"
                v-can-write
                @click="openQuarantineModal"
                class="w-full bg-red-600 text-white text-xl font-semibold py-4 rounded-2xl hover:bg-red-700 transition"
              >
                Зафиксировать несоответствие
              </button>
            </div>
          </div>
        </div>
      </main>
    </template>
    <template v-else-if="deskBusy">
      <div class="flex justify-center items-center h-screen">
        <p class="text-lg font-medium text-red-600">Данный рабочий стол занят{{ deskOwnerName ? ' — ' + deskOwnerName : '' }}</p>
      </div>
    </template>
    <template v-else>
      <div class="flex justify-center items-center h-screen px-6">
        <!-- AND-215: очередь задания ещё не дошла до операции этого стола -->
        <p v-if="waitingForOperation" class="text-lg font-medium text-neutral-900 text-center">
          Ожидаем завершения операции «{{ waitingForOperation.operation_name }}» по заданию № {{ waitingForOperation.task_number }}. Как только её закроют, задание появится на этом столе.
        </p>
        <p v-else class="text-lg font-medium text-neutral-900">На данный момент нет операций</p>
      </div>
    </template>
  </div>
  <div class="hidden">
    <video ref="video" autoplay playsinline></video>
    <canvas ref="canvas"></canvas>
  </div>

  <!-- Окно предпросмотра камеры -->
  <div
    v-if="showCameraPreview"
    class="fixed inset-0 z-[60] flex items-center justify-center bg-black/70 p-4"
  >
    <div class="bg-white rounded-2xl shadow-2xl w-full max-w-3xl overflow-hidden flex flex-col">
      <div class="px-6 py-4 border-b border-neutral-200 flex items-center justify-between">
        <h3 class="text-xl font-semibold text-neutral-900">
          {{ capturedPhotoUrl ? 'Предпросмотр снимка' : 'Наведите камеру' }}
        </h3>
        <button
          @click="closeCameraPreview"
          class="text-neutral-400 hover:text-neutral-700 text-2xl leading-none"
          title="Закрыть"
        >
          ✕
        </button>
      </div>
      <div class="p-6">
        <div class="aspect-video relative border rounded-lg overflow-hidden bg-neutral-900 w-full">
          <video
            v-show="!capturedPhotoUrl"
            ref="previewVideo"
            autoplay
            playsinline
            muted
            class="absolute inset-0 w-full h-full object-contain"
          ></video>
          <img
            v-if="capturedPhotoUrl"
            :src="capturedPhotoUrl"
            alt="Снимок"
            class="absolute inset-0 w-full h-full object-contain"
          />
        </div>
      </div>
      <div class="px-6 py-4 border-t border-neutral-200 flex gap-3 justify-end">
        <template v-if="!capturedPhotoUrl">
          <button
            @click="closeCameraPreview"
            class="px-6 py-3 rounded-xl bg-neutral-200 text-neutral-700 text-lg font-semibold hover:bg-neutral-300 transition"
          >
            Отмена
          </button>
          <button
            @click="capturePreviewPhoto"
            :disabled="!cameraReady"
            class="px-6 py-3 rounded-xl bg-amber-500 text-white text-lg font-semibold hover:bg-amber-600 transition disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
          >
            <CameraIcon class="w-6 h-6" />
            Сделать снимок
          </button>
        </template>
        <template v-else>
          <button
            @click="retakePhoto"
            :disabled="isSavingPhoto"
            class="px-6 py-3 rounded-xl bg-neutral-200 text-neutral-700 text-lg font-semibold hover:bg-neutral-300 transition disabled:opacity-50"
          >
            Переснять
          </button>
          <button
            @click="confirmCapturedPhoto"
            :disabled="isSavingPhoto"
            class="px-6 py-3 rounded-xl bg-green-600 text-white text-lg font-semibold hover:bg-green-700 transition disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {{ isSavingPhoto ? 'Сохранение...' : 'Сохранить фото' }}
          </button>
        </template>
      </div>
    </div>
  </div>
  <!-- Модальное окно перехода на следующий шаг -->
  <NextStepModal
    :is-visible="isModalVisible"
    :title="modalTitle"
    @close="closeModal"
    @confirm="confirmModal"
  />
  <!-- Галерея фото операции -->
  <ImageModal
    v-if="showPhotoGallery && photoGalleryImages.length > 0"
    :images="photoGalleryImages"
    :readonly="true"
    :initial-slide="photoGalleryImages.length - 1"
    title="Фото шага"
    @remove-image="removePhoto"
    @close="showPhotoGallery = false"
  />
  <!-- Модалка предупреждения о нераспределённых материалах -->
  <div v-if="showPackingWarning" class="fixed inset-0 bg-black/50 z-50 flex items-center justify-center">
    <div class="bg-white rounded-lg shadow-xl p-8 w-[540px]">
      <h3 class="text-xl font-semibold mb-3">Не все материалы распределены</h3>
      <!-- Перечень недостающих позиций приходит с сервера, иначе — общий текст -->
      <p
        v-if="packingWarningMessage"
        class="text-base text-gray-600 mb-6 whitespace-pre-line break-words max-h-64 overflow-y-auto"
      >{{ packingWarningMessage }}</p>
      <p v-else class="text-base text-gray-600 mb-6">Не все материалы размещены по упаковочным местам. Необходимо вернуться к упаковке и распределить все материалы.</p>
      <div class="flex gap-3">
        <button
          @click="showPackingWarning = false; packingWarningMessage = ''; completeOperation(true)"
          class="flex-1 px-6 py-3 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 text-base font-medium"
        >
          Всё равно завершить
        </button>
        <button
          @click="showPackingWarning = false; packingWarningMessage = ''; goToPackingPlaces()"
          class="flex-1 px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-base font-medium"
        >
          Вернуться к упаковке
        </button>
      </div>
    </div>
  </div>
  <!-- Модалка предупреждения о невзятых комплектующих.
       Обхода нет: недоукомплектованное изделие не должно уходить дальше по маршруту -->
  <div v-if="showPickingWarning" class="fixed inset-0 bg-black/50 z-50 flex items-center justify-center">
    <div class="bg-white rounded-lg shadow-xl p-8 w-[540px]">
      <h3 class="text-xl font-semibold mb-3">Комплектование не завершено</h3>
      <!-- Перечень невзятых позиций приходит с сервера, иначе — общий текст -->
      <p
        v-if="pickingWarningMessage"
        class="text-base text-gray-600 mb-6 whitespace-pre-line break-words max-h-64 overflow-y-auto"
      >{{ pickingWarningMessage }}</p>
      <p v-else class="text-base text-gray-600 mb-6">Взяты не все комплектующие по заданию. Завершить операцию можно только после того, как всё будет собрано.</p>
      <div class="flex">
        <button
          @click="showPickingWarning = false; pickingWarningMessage = ''"
          class="flex-1 px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-base font-medium"
        >
          Вернуться к комплектованию
        </button>
      </div>
    </div>
  </div>
  <!-- Модальное окно настройки клавиши фото -->
  <div v-if="showKeybindModal" class="fixed inset-0 bg-black/50 z-40 flex items-center justify-center" @click.self="closeKeybindModal">
    <div class="bg-white rounded-2xl shadow-xl p-8 max-w-md w-full mx-4 text-center">
      <h3 class="text-2xl font-semibold mb-4">Настройка клавиши</h3>
      <p class="text-lg text-neutral-600 mb-6">Нажмите любую клавишу или педаль для привязки</p>
      <div class="text-4xl font-bold text-blue-600 mb-6 h-16 flex items-center justify-center border-2 border-dashed border-blue-200 rounded-xl">
        {{ hotkeyLabel }}
      </div>
      <div class="flex gap-3 justify-center">
        <button @click="closeKeybindModal" class="px-6 py-3 bg-neutral-200 text-neutral-700 rounded-xl hover:bg-neutral-300 transition text-lg">
          Закрыть
        </button>
        <button @click="resetHotkey" class="px-6 py-3 bg-red-100 text-red-700 rounded-xl hover:bg-red-200 transition text-lg">
          Сбросить
        </button>
      </div>
    </div>
  </div>

    <!-- Модалка подтверждения перехода на другую задачу -->
    <div v-if="showSwitchModal" class="fixed inset-0 bg-black/50 z-50 flex items-center justify-center" @click.self="cancelSwitch">
      <div class="bg-white rounded-2xl shadow-xl p-8 max-w-md w-full mx-4 text-center">
        <h3 class="text-2xl font-semibold mb-4">Перейти к другой задаче?</h3>
        <p class="text-lg text-neutral-600 mb-6">
          Заказ #{{ pendingSwitchItem?.production_order_id }} — {{ pendingSwitchItem?.product_name }}
        </p>
        <div class="flex gap-3 justify-center">
          <button @click="cancelSwitch" class="px-6 py-3 bg-neutral-200 text-neutral-700 rounded-xl hover:bg-neutral-300 transition text-lg">
            Отмена
          </button>
          <button @click="confirmSwitch" class="px-6 py-3 bg-blue-600 text-white rounded-xl hover:bg-blue-700 transition text-lg">
            Перейти
          </button>
        </div>
      </div>
    </div>

    <!-- Модалка «задача занята» -->
    <div v-if="showBusyItemModal" class="fixed inset-0 bg-black/50 z-50 flex items-center justify-center" @click.self="closeBusyItemModal">
      <div class="bg-white rounded-2xl shadow-xl p-8 max-w-md w-full mx-4 text-center">
        <h3 class="text-2xl font-semibold mb-4 text-red-600">Задача занята</h3>
        <p class="text-lg text-neutral-600 mb-6">
          Эта задача уже в работе{{ busyItemOwnerName ? ' — ' + busyItemOwnerName : '' }}.
        </p>
        <div class="flex justify-center">
          <button @click="closeBusyItemModal" class="px-6 py-3 bg-neutral-200 text-neutral-700 rounded-xl hover:bg-neutral-300 transition text-lg">
            Понятно
          </button>
        </div>
      </div>
    </div>

    <!-- Модалка «сначала завершите текущую операцию» -->
    <div v-if="showActiveOpModal" class="fixed inset-0 bg-black/50 z-50 flex items-center justify-center" @click.self="closeActiveOpModal">
      <div class="bg-white rounded-2xl shadow-xl p-8 max-w-md w-full mx-4 text-center">
        <h3 class="text-2xl font-semibold mb-4">Операция в работе</h3>
        <p class="text-lg text-neutral-600 mb-6">
          Сначала завершите или остановите текущую операцию — тогда можно перейти к другой задаче.
        </p>
        <div class="flex justify-center">
          <button @click="closeActiveOpModal" class="px-6 py-3 bg-neutral-200 text-neutral-700 rounded-xl hover:bg-neutral-300 transition text-lg">
            Понятно
          </button>
        </div>
      </div>
    </div>

    <!-- Модалка «сессия истекла» (лок операции потерян) -->
    <div v-if="showSessionExpired" class="fixed inset-0 bg-black/50 z-50 flex items-center justify-center">
      <div class="bg-white rounded-2xl shadow-xl p-8 max-w-md w-full mx-4 text-center">
        <h3 class="text-2xl font-semibold mb-4 text-red-600">Сессия истекла</h3>
        <p class="text-lg text-neutral-600 mb-6">
          Бронь операции была потеряна из-за перерыва в связи или бездействия. Если задачу никто не занял — можно продолжить с того же места.
        </p>
        <div class="flex gap-3 justify-center">
          <button @click="closeSessionExpired" :disabled="resumingSession" class="px-6 py-3 bg-neutral-200 text-neutral-700 rounded-xl hover:bg-neutral-300 transition text-lg disabled:opacity-50">
            Закрыть
          </button>
          <button @click="resumeSession" :disabled="resumingSession" class="px-6 py-3 bg-green-600 text-white rounded-xl hover:bg-green-700 transition text-lg disabled:opacity-50">
            {{ resumingSession ? 'Продолжаем…' : 'Продолжить' }}
          </button>
        </div>
      </div>
    </div>

    <!-- Модалка карантина производства: подтверждение + краткое описание несоответствия -->
    <div v-if="showQuarantineModal" class="fixed inset-0 bg-black/50 z-50 flex items-center justify-center" @click.self="closeQuarantineModal">
      <div class="bg-white rounded-2xl shadow-xl p-8 max-w-2xl w-full mx-4">
        <h3 class="text-2xl font-semibold mb-4 text-red-600">Зафиксировать несоответствие?</h3>
        <p class="text-lg text-neutral-600 mb-6">
          Изделие будет остановлено и отправлено на склад карантина, а служба качества
          получит случай несоответствия. Остальные изделия заказа продолжат работу.
        </p>
        <label class="block text-lg font-medium text-neutral-700 mb-2">
          Причина несоответствия <span class="text-red-600">*</span>
        </label>
        <textarea
          v-model="quarantineDescription"
          rows="4"
          maxlength="2000"
          placeholder="Что именно не соответствует требованиям"
          class="w-full border border-neutral-300 rounded-xl px-4 py-3 text-lg focus:outline-none focus:ring-2 focus:ring-red-500"
        ></textarea>
        <p class="text-sm text-neutral-500 mt-1">{{ quarantineDescription.length }} из 2000 символов</p>
        <p v-if="quarantineError" class="text-base text-red-600 mt-3 whitespace-pre-line break-words">{{ quarantineError }}</p>
        <div class="flex gap-3 justify-end mt-6">
          <button
            @click="closeQuarantineModal"
            :disabled="quarantineSaving"
            class="px-6 py-3 bg-neutral-200 text-neutral-700 rounded-xl hover:bg-neutral-300 transition text-lg disabled:opacity-50"
          >
            Отмена
          </button>
          <button
            @click="quarantineFromOtk"
            :disabled="quarantineSaving"
            class="px-6 py-3 bg-red-600 text-white rounded-xl hover:bg-red-700 transition text-lg disabled:opacity-50"
          >
            {{ quarantineSaving ? 'Фиксируем…' : 'Зафиксировать несоответствие' }}
          </button>
        </div>
      </div>
    </div>

    <!-- Просмотр изображения на весь экран: открывается поверх, операцию не прерывает (Z-50) -->
    <FullscreenImageViewer :src="fullscreenImageSrc" @close="fullscreenImageSrc = ''" />
</template>

<script>
import {useRouter} from 'vue-router';
import axios from 'axios';
import { useAuthStore } from '@/stores/auth';
import NextStepModal from '@/components/NextStepModal.vue';
import PickingComponents from '@/components/production/PickingComponents.vue';
import { CameraIcon, Cog6ToothIcon, PhotoIcon } from '@heroicons/vue/24/outline';
import ImageModal from '@/components/production/techMaps/ImageModal.vue';
import PackingPlaces from '@/pages/workPlace/PackingPlaces.vue';
import PackingPlacesSummary from '@/components/production/PackingPlacesSummary.vue';
import FullscreenImageViewer from '@/components/common/FullscreenImageViewer.vue';

export default {
  props: {
    id: [String, Number],
  },
  components: { NextStepModal, PickingComponents, CameraIcon, Cog6ToothIcon, PhotoIcon, ImageModal, PackingPlaces, PackingPlacesSummary, FullscreenImageViewer },
  data() {
    return {
      // Изображение, открытое на весь экран; пустая строка — просмотрщик скрыт (Z-50)
      fullscreenImageSrc: '',
      activeView: 'default',
      isPrinting: false,
      userStore: useAuthStore(),
      loading: true, // Флаг загрузки
      taskData: null, // Данные задачи
      currentStepIndex: 0, // Индекс текущего шага
      isOperationStarted: false, // Флаг начала операции
      presenceTimer: null, // AND-193: интервал пульса присутствия (heartbeat лока операции)
      deskBusy: false, // AND-193: рабочий стол занят другим сотрудником
      deskOwnerName: '', // Имя занявшего стол
      waitingForOperation: null, // AND-215: какой операции ждёт стол, когда очередь задания до него не дошла
      selectedOperationId: null, // Выбранная из очереди операция (выбираемая очередь)
      showSwitchModal: false, // Модалка подтверждения перехода на другую задачу
      pendingSwitchItem: null, // Задача, на которую подтверждаем переход
      showBusyItemModal: false, // Модалка «задача занята»
      busyItemOwnerName: '', // Имя занявшего выбранную задачу
      showActiveOpModal: false, // Модалка «сначала завершите текущую операцию»
      showSessionExpired: false, // Модалка «сессия истекла» (лок операции потерян)
      resumingSession: false, // Идёт попытка продолжить прерванную операцию (повторный захват лока)
      videoStream: null,
      intervalId: null,
      cameraReady: false,
      videoReady: false,
      isModalVisible: false, // Флаг видимости модального окна
      modalTitle: '',
      hasPendingPickingItems: false, // Есть несохранённые позиции комплектования
      isSavingProgress: false, // Идёт сохранение прогресса
      pickedItemsData: [], // Данные отсканированных позиций для списания
      stepImageCarouselIndex: 0, // Текущий слайд карусели изображений шага
      photoHotkeyCode: 'Space', // Код клавиши для фото (по умолчанию пробел)
      photoGalleryByStep: {}, // Фото по шагам: { [operationStepId]: [{url, photoId}] }
      photoFlash: false, // Флаг анимации вспышки
      isPhotoTaking: false, // Флаг процесса фотографирования
      showKeybindModal: false, // Флаг модалки настройки клавиши
      showPhotoGallery: false, // Флаг отображения галереи
      showPackingWarning: false, // Модалка предупреждения о нераспределённых материалах
      packingWarningMessage: '', // Сообщение сервера с перечнем нераспределённых позиций
      showPickingWarning: false, // Модалка предупреждения о невзятых комплектующих
      pickingWarningMessage: '', // Сообщение сервера с перечнем невзятых позиций
      showCameraPreview: false, // Флаг окна предпросмотра камеры
      capturedPhotoBlob: null, // Зафиксированный кадр (blob) до подтверждения
      capturedPhotoUrl: null, // Object URL зафиксированного кадра для предпросмотра
      isSavingPhoto: false, // Идёт загрузка подтверждённого фото
      stepDocuments: [], // Прикреплённые документы { stepId, file_id, name, preview_url }
      isUploadingDocument: false, // Идёт загрузка документа
      serialDraft: '', // Черновик серийного номера изделия
      serialSaving: false, // Идёт сохранение серийного номера
      isSerialEditing: false, // Режим редактирования серийного номера
      showQuarantineModal: false, // Модалка подтверждения перемещения изделия в карантин
      quarantineDescription: '', // Краткое описание несоответствия
      quarantineSaving: false, // Идёт отправка в карантин
      quarantineError: '', // Текст ошибки от сервера или локальной валидации
    };
  },
  setup() {
    const router = useRouter(); // Инициализация Vue Router
    return {router};
  },
  computed: {
    user() {
      return this.userStore?.user; // Получаем данные пользователя из Pinia
    },
    currentStep() {
      return this.taskData?.steps[this.currentStepIndex];
    },
    productionTaskOperationId() {
      return this.taskData?.id;
    },
    currentStepDocuments() {
      const stepId = this.currentStep?.operation_step?.id;
      if (!stepId) return [];
      return this.stepDocuments.filter(doc => doc.stepId === stepId);
    },
    currentModule() {
      if (!this.user?.modules) return null;
      return this.user.modules.find(module => module.id == this.$route.params.id);
    },
    isPickingDeskModule() {
      return this.currentModule?.key === 'picking-desk';
    },
    isPickingOperation() {
      // Операция комплектования — по типу текущего шага
      return this.currentStep?.operation_step?.type === 'picking';
    },
    hasComponents() {
      // Проверяем что у шага есть компоненты
      return this.currentStep?.operation_step?.components?.length > 0;
    },
    isPickingBlockVisible() {
      // Блок комплектования на экране. Кнопка «Сохранить» привязана к тому же условию:
      // без блока сохранять нечего — компонента на странице нет.
      // Шаг «Используемое ПО» комплектованием не является даже на столе комплектовщика
      return this.isOperationStarted
        && (this.isPickingOperation || this.isPickingDeskModule)
        && this.hasComponents
        && !this.isSoftwareStep;
    },
    isSoftwareStep() {
      // Шаг «Используемое ПО» — показывает сборщику список ПО с ключами активации
      return this.currentStep?.operation_step?.type === 'software';
    },
    // ПО текущего шага с ключами активации: состав берём из компонентов шага,
    // ключи — из данных изделия (одним запросом вместе с заданием)
    currentStepSoftwareKeys() {
      const components = this.currentStep?.operation_step?.components || [];
      const keys = this.taskData?.software_keys || [];

      return components
        .filter(component => component.product?.id)
        .map(component => {
          const row = keys.find(key => key.product_id === component.product.id);
          return {
            product_id: component.product.id,
            name: row?.name || component.product?.name || '',
            activation_key: row?.activation_key || null,
          };
        });
    },
    hasPackagingStep() {
      // Проверяем что операция содержит упаковочный шаг (любой шаг, не только текущий)
      return this.taskData?.steps?.some(s => s.operation_step?.type === 'packaging') || false;
    },
    isPackagingStep() {
      return this.currentStep?.operation_step?.type === 'packaging';
    },
    stepImages() {
      const op = this.currentStep?.operation_step;
      if (!op) return [];
      if (Array.isArray(op.images) && op.images.length > 0) return op.images;
      return [];
    },
    currentStepImageSrc() {
      const img = this.stepImages[this.stepImageCarouselIndex];
      return img?.url || '';
    },
    /** Адрес изображения изделия: относительный путь лежит в /images (Z-50) */
    productImageSrc() {
      const url = this.taskData?.img_url;
      if (!url) return '';

      return url.startsWith('/') || url.startsWith('http') ? url : `/images/${url}`;
    },
    hotkeyLabel() {
      return this.getHotkeyLabel(this.photoHotkeyCode);
    },
    // Есть ли сохранённый прогресс по операции (была начата ранее, но не завершена)
    hasSavedProgress() {
      const steps = this.taskData?.steps || [];
      return this.taskData?.operation_status === 'work'
        || steps.some(s => s.status === 'work' || s.status === 'done');
    },
    // Ключ галереи — id шага спецификации (operation_step), к которому привязаны фото
    currentStepKey() {
      return this.currentStep?.operation_step?.id ?? null;
    },
    // Фото текущего шага
    currentStepGallery() {
      const key = this.currentStepKey;
      if (key == null) return [];
      return this.photoGalleryByStep[key] || [];
    },
    photoCount() {
      return this.currentStepGallery.length;
    },
    photoGalleryImages() {
      return this.currentStepGallery.map(item => ({ url: item.url }));
    }
  },
  created() {
    this.fetchTaskData();
    this.initCamera();
    const savedHotkey = localStorage.getItem('photo_hotkey');
    if (savedHotkey) {
      this.photoHotkeyCode = savedHotkey;
    }
  },
  watch: {
    currentStepIndex() {
      this.stepImageCarouselIndex = 0;
      this.showPhotoGallery = false; // у каждого шага своя галерея
    },
    taskData(newTaskData, oldTaskData) {
      // Если taskData изменился, переинициализируем камеру
      if (newTaskData && newTaskData !== oldTaskData) {
        this.stopCamera(); // Останавливаем предыдущую камеру, если она была
        this.initCamera(); // Инициализируем камеру заново
      }
    }
  },
  mounted() {
    document.addEventListener('keydown', this.onKeyDown);
    window.addEventListener('beforeunload', this.handleBeforeUnload);
  },
  beforeUnmount() {
    document.removeEventListener('keydown', this.onKeyDown);
    window.removeEventListener('beforeunload', this.handleBeforeUnload);
    this.stopInterval();
    this.stopPresence(); // AND-193
    // Лок ставится только на старте/продолжении — освобождаем лишь если операция была в работе
    if (this.isOperationStarted) {
      this.releaseCurrentLock(); // AND-193: освобождаем операцию и стол при уходе (SPA-навигация)
    }
    this.stopCamera();
    this.clearCapturedPhoto();
  },
  methods: {
    formatQuantity(val) {
      if (val == null || val === undefined) return '0';
      const num = Number(val);
      return isNaN(num) ? '0' : (Math.round(num * 1000) / 1000).toString();
    },
    getUnitLabel(product) {
      return product?.unit_short_name || product?.unit?.short_name || 'шт';
    },
    checkPopupBlocked() {
      const testPopup = window.open('', '_blank');
      if (!testPopup || testPopup.closed || typeof testPopup.closed === 'undefined') {
        alert('Пожалуйста, разрешите всплывающие окна для этого сайта');
        return false;
      }
      testPopup.close();
      return true;
    },

    fallbackPrint(html) {
      const iframe = document.createElement('iframe');
      iframe.style.display = 'none';
      document.body.appendChild(iframe);

      const iframeDoc = iframe.contentDocument || iframe.contentWindow.document;
      iframeDoc.write(html);
      iframeDoc.close();

      setTimeout(() => {
        iframe.contentWindow.focus();
        iframe.contentWindow.print();
        setTimeout(() => document.body.removeChild(iframe), 1000);
      }, 500);
    },

    async printDocument() {
      if (!this.checkPopupBlocked()) {
        return;
      }

      this.isPrinting = true;

      try {
        const step = this.currentStep;

        if (step?.operation_step?.type !== 'print') {
          throw new Error('Этот шаг не предназначен для печати');
        }

        if (!step?.operation_step?.documents?.length) {
          throw new Error('Для этого шага не назначены документы');
        }

        // Обогащаем компоненты данными о взятых позициях
        const enrichedComponents = this.enrichComponentsWithPickedItems(step.operation_step.components);

        for (const [index, document] of step.operation_step.documents.entries()) {
          await this.printSingleDocument(document, enrichedComponents, index);
          if (index < step.operation_step.documents.length - 1) {
            await new Promise(resolve => setTimeout(resolve, 1000));
          }
        }
      } catch (error) {
        console.error('Ошибка печати:', error);
        alert(`Ошибка печати: ${error.message}`);
      } finally {
        this.isPrinting = false;
      }
    },

    async printSingleDocument(document, components, index) {
      if (!document?.id || !this.taskData?.batch_product_id) {
        console.error('Печать: отсутствует document.id или batch_product_id');
        return Promise.resolve();
      }
      return new Promise((resolve) => {
        axios.post('/api/documents/render', {
          document_id: document.id,
          batch_product_id: this.taskData.batch_product_id,
          operation_step_id: this.currentStep?.operation_step?.id,
          production_task_operation_id: this.taskData?.id,
          // template: document.template_key,
          // document: document,
          // task_data: {
          //   product_id: this.taskData.product_id,
          //   batch_product_id: this.taskData.batch_product_id,
          // },
          // is_multiple: true,
          // document_index: index,
          // document_count: this.currentStep?.operation_step?.documents?.length || 1,
        }).then(response => {
          const printWindow = window.open('', '_blank');

          if (!printWindow) {
            throw new Error('Не удалось открыть окно печати. Разрешите всплывающие окна.');
          }

          printWindow.document.write(response.data.html);
          printWindow.document.close();

          printWindow.onload = () => {
            setTimeout(() => {
              try {
                printWindow.print();
                setTimeout(() => {
                  if (!printWindow.closed) {
                    printWindow.close();
                  }
                  resolve();
                }, 1000);
              } catch (error) {
                console.error('Print error:', error);
                if (!printWindow.closed) {
                  printWindow.close();
                }
                resolve();
              }
            }, 500);
          };
        }).catch(error => {
          console.error(`Ошибка печати документа ${document.name}:`, error);
          resolve();
        });
      });
    },

    // Метод для получения данных задачи с бэкенда
    async fetchTaskData() {
      this.pickedItemsData = [];
      this.loading = true;
      try {
        const id = this.$route.params.id; // Получаем ID из параметров роута
        const response = await axios.post('/api/workshopPostUnit/getTaskForWorkshopPostUnit', {
          id,
          preferred_operation_id: this.selectedOperationId || undefined,
        });
        if (response.data.success) {
          // Стол занят другим сотрудником (кто-то начал операцию за этим столом)
          if (response.data.desk_busy) {
            this.deskBusy = true;
            this.deskOwnerName = response.data.desk_owner_name || '';
            this.waitingForOperation = null;
            this.taskData = null;
            this.stopPresence();
            return;
          }
          this.deskBusy = false;
          this.deskOwnerName = '';
          this.waitingForOperation = response.data.waiting_for || null;
          this.taskData = response.data.data; // Сохраняем данные задачи
          this.serialDraft = this.taskData?.serial_number || ''; // Черновик серийного номера
          // Наполняем список прикреплённых документов по шагам (из ранее загруженных)
          this.stepDocuments = [];
          (this.taskData?.steps || []).forEach((step) => {
            const stepId = step.operation_step?.id;
            (step.uploaded_documents || []).forEach((doc) => {
              this.stepDocuments.push({ stepId, ...doc });
            });
          });
          this.restoreOperationProgress(); // Восстанавливаем прогресс по шагам
          await this.loadOperationPhotos(); // Подгружаем фото по шагам
          // Пульс присутствия при заходе НЕ запускаем — бронь ставится только при старте/продолжении.
        } else {
          console.error('Ошибка при получении данных:', response.data.message);
        }
      } catch (error) {
        console.error('Ошибка при запросе данных:', error);
      } finally {
        this.loading = false;
      }
    },

    // Восстановление позиции прогресса при (повторном) открытии.
    // Важно: операцию НЕ запускаем автоматически — выработка стартует только
    // по кнопке «Начать операцию» / «Продолжить операцию».
    restoreOperationProgress() {
      this.isOperationStarted = false;
      const steps = this.taskData?.steps || [];
      if (!steps.length) {
        this.currentStepIndex = 0;
        return;
      }

      // Позиция возобновления — первый незавершённый шаг; если все завершены — последний
      let idx = steps.findIndex(s => s.status !== 'done');
      if (idx === -1) idx = steps.length - 1;
      this.currentStepIndex = idx;
    },

    // Загрузка ранее сделанных фото, сгруппированных по шагам
    async loadOperationPhotos() {
      const opId = this.productionTaskOperationId;
      if (!opId) {
        this.photoGalleryByStep = {};
        return;
      }
      try {
        const { data } = await axios.post('/api/productionOperation/photos', { operationId: opId });
        const byStep = {};
        if (data.success && Array.isArray(data.photos)) {
          data.photos.forEach(p => {
            const key = p.operation_step_id;
            if (key == null) return; // фото без привязки к шагу в галерее по шагам не показываем
            if (!byStep[key]) byStep[key] = [];
            byStep[key].push({ url: '/' + String(p.path).replace(/^\//, ''), photoId: p.id });
          });
        }
        this.photoGalleryByStep = byStep;
      } catch (e) {
        console.error('Ошибка загрузки фото операции:', e);
        this.photoGalleryByStep = {};
        alert('Не удалось загрузить ранее сделанные фото операции.');
      }
    },

    // Форматирование даты
    formatDate(dateString) {
      if (!dateString) return '';
      const date = new Date(dateString);
      return `${date.getDate().toString().padStart(2, '0')}.${(date.getMonth() + 1).toString().padStart(2, '0')}.${date.getFullYear()}`;
    },

    // Инициализация камеры
    async initCamera() {
      try {
        if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
          console.warn("getUserMedia не поддерживается в этом браузере.");
          this.cameraReady = false;
          return;
        }
        // Запрос на доступ к камере
        this.videoStream = await navigator.mediaDevices.getUserMedia({ video: true });
        this.$refs.video.srcObject = this.videoStream;

        this.$refs.video.onplay = () => {
          if (this.$refs.video.videoWidth > 0 && this.$refs.video.videoHeight > 0) {
            // Камера готова
            this.videoReady = true;
            this.cameraReady = true;
          } else {
            console.error("Камера не готова для снимка.");
          }
        };
      } catch (err) {
        console.error("Ошибка доступа к камере:", err);
        this.stopInterval(); // Остановка интервала при ошибке
        this.cameraReady = false;
      }
    },

    async takePhotoAndUpload() {
      if (!this.cameraReady) {
        console.warn("Камера не готова, фото не будет сделано.");
        return null;
      }
      const photoBlob = await this.takePhoto();
      if (photoBlob) {
        return await this.uploadPhoto(photoBlob);
      }
      return null;
    },

    // Снимок фото
    async takePhoto() {
      if (!this.$refs.video || !this.$refs.canvas) {
        console.warn("Отсутствуют элементы video или canvas.");
        return null;
      }
      // Проверка готовности камеры
      if (!this.videoReady) {
        console.error("Камера ещё не готова.");
        return null;
      }

      const video = this.$refs.video;
      const canvas = this.$refs.canvas;

      if (video.videoWidth === 0 || video.videoHeight === 0) {
        return null;
      }

      // Настройка canvas
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;
      const ctx = canvas.getContext('2d');
      ctx.drawImage(video, 0, 0);

      return new Promise(resolve => {
        canvas.toBlob(blob => {
          if (blob) {
            resolve(blob);
          } else {
            console.error("Ошибка при создании фото.");
            resolve(null); // Если blob не создан
          }
        }, 'image/jpeg');
      });
    },

    // Загрузка фото
    async uploadPhoto(blob) {
      if (!blob) {
        console.error("Ошибка: файл не создан.");
        return null;
      }

      const formData = new FormData();
      formData.append('file', blob, `photo_${Date.now()}.jpg`);
      formData.append('production_task_operation_id', this.productionTaskOperationId);
      const stepId = this.currentStepKey;
      if (stepId != null) formData.append('operation_step_id', stepId);

      try {
        const response = await axios.post('/api/productionOperation/uploadOperationPhoto', formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
        if (response.data.success) {
          return {
            path: response.data.path,
            photoId: response.data.photo_id,
            operationStepId: response.data.operation_step_id ?? stepId,
          };
        }
        console.error("Ошибка при загрузке фото:", response.data.message);
        return null;
      } catch (err) {
        console.error("Ошибка загрузки фото:", err.response ? err.response.data : err);
        return null;
      }
    },

    startInterval() {
      if (this.intervalId) {
        this.stopInterval();
      }
      this.intervalId = setInterval(this.takePhotoAndUpload, 30000);
    },

    stopInterval() {
      if (this.intervalId) {
        clearInterval(this.intervalId);
        this.intervalId = null;
      }
    },

    // AND-193: пульс присутствия — продлеваем лок текущей операции, пока вкладка открыта.
    startPresence() {
      this.stopPresence();
      if (!this.taskData?.id) return;
      this.presenceTimer = setInterval(() => {
        if (!this.taskData?.id) return;
        axios.post('/api/productionOperation/heartbeatLock', {
          operation_id: this.taskData.id,
          module_id: this.$route.params.id,
        }).then((response) => {
          // Лок операции потерян (перехвачен другим или истёк по таймауту) — прекращаем работу
          if (response.data && response.data.success === false && this.isOperationStarted) {
            this.handleSessionLost();
          }
        }).catch(() => {});
      }, 15000);
    },
    stopPresence() {
      if (this.presenceTimer) {
        clearInterval(this.presenceTimer);
        this.presenceTimer = null;
      }
    },
    // AND-193: бронь операции потеряна — блокируем дальнейшую работу и сообщаем сотруднику
    handleSessionLost() {
      this.stopPresence();
      this.isOperationStarted = false;
      this.showSessionExpired = true;
    },

    // Выбираемая очередь: клик по задаче в очереди
    selectQueueItem(item) {
      if (!item || item.operation_id === this.taskData?.id) return; // текущая — ничего
      if (item.is_busy) { // занята другим — модалка «занято»
        this.busyItemOwnerName = item.owner_name || '';
        this.showBusyItemModal = true;
        return;
      }
      // Свободная другая: перейти можно только если текущая операция не в работе
      if (this.isOperationStarted) {
        this.showActiveOpModal = true;
        return;
      }
      this.pendingSwitchItem = item;
      this.showSwitchModal = true;
    },
    closeActiveOpModal() {
      this.showActiveOpModal = false;
    },
    closeSessionExpired() {
      // Закрытие модалки «сессия истекла» — перезагружаем задачу (подберётся заново/следующая)
      this.showSessionExpired = false;
      this.selectedOperationId = null;
      this.fetchTaskData();
    },
    // AND-193: продолжить прерванную операцию — заново берём лок той же операции.
    // Данные не перезагружались, поэтому при успехе работа продолжается с текущего места
    // без потери прогресса. Если операцию/стол уже занял другой — продолжить нельзя.
    async resumeSession() {
      if (!this.taskData?.id) {
        this.closeSessionExpired();
        return;
      }
      this.resumingSession = true;
      try {
        await axios.post('/api/productionOperation/acquireLock', {
          operation_id: this.taskData.id,
          module_id: this.$route.params.id,
        });
        // Лок снова наш — возобновляем работу ровно с того места, где прервались
        this.showSessionExpired = false;
        this.isOperationStarted = true;
        this.startPresence();
      } catch (e) {
        // Операцию или стол уже занял другой сотрудник — продолжить нельзя, подбираем заново
        this.showSessionExpired = false;
        alert('Продолжить не удалось — задачу уже занял другой сотрудник.');
        this.selectedOperationId = null;
        await this.fetchTaskData();
      } finally {
        this.resumingSession = false;
      }
    },
    async confirmSwitch() {
      const item = this.pendingSwitchItem;
      this.showSwitchModal = false;
      this.pendingSwitchItem = null;
      if (!item) return;
      this.selectedOperationId = item.operation_id;
      await this.fetchTaskData();
    },
    cancelSwitch() {
      this.showSwitchModal = false;
      this.pendingSwitchItem = null;
    },
    closeBusyItemModal() {
      this.showBusyItemModal = false;
      this.busyItemOwnerName = '';
    },
    // Снять лок текущей операции (best-effort при уходе со страницы)
    releaseCurrentLock() {
      const operationId = this.taskData?.id;
      if (!operationId) return;
      axios.post('/api/productionOperation/releaseLock', {
        operation_id: operationId,
        module_id: this.$route.params.id,
      }).catch(() => {});
    },

    // Останавливаем камеру
    stopCamera() {
      if (this.videoStream) {
        this.videoStream.getTracks().forEach(track => {
          track.stop();
        });
        this.$refs.video.srcObject = null;
        this.videoStream = null;
        this.cameraReady = false;
        this.videoReady = false;
        console.log('Камера остановлена');
      }
    },

    // Сохранение статуса шага на сервере + локально (статусы: pending|work|done)
    async setStepStatus(stepId, status) {
      this.updateLocalStepStatus(stepId, status);
      try {
        await axios.post('/api/productionOperation/changeStepStatus', {
          step_id: stepId,
          status,
        });
        return true;
      } catch (error) {
        console.error('Ошибка при обновлении статуса шага:', error);
        alert('Не удалось сохранить статус шага. Проверьте соединение — при повторном входе прогресс может откатиться.');
        return false;
      }
    },

    // AND-193: бронь операции за собой (лок ставится только здесь — при старте/продолжении).
    // Возвращает true, если удалось; false — если уже занята другим (показываем следующую).
    async acquireLock() {
      if (!this.taskData?.id) return false;
      try {
        await axios.post('/api/productionOperation/acquireLock', {
          operation_id: this.taskData.id,
          module_id: this.$route.params.id,
        });
        return true;
      } catch (e) {
        if (e.response?.status === 409) {
          alert('Эту задачу уже начал другой сотрудник. Показываю следующую доступную.');
          await this.fetchTaskData();
          return false;
        }
        // Операция недоступна: очередь задания до неё не дошла либо она не относится к столу
        if (e.response?.status === 422) {
          alert(e.response?.data?.message || 'Операция сейчас недоступна');
          await this.fetchTaskData();
          return false;
        }
        return true; // прочие ошибки не блокируют работу
      }
    },

    // Логика кнопки "Начать операцию"
    async startOperation() {
      if (!this.isOperationStarted) {
        // Сначала бронируем; если занято другим — не стартуем, показываем следующую
        if (!await this.acquireLock()) return;
        this.isOperationStarted = true;
        this.startPresence(); // пульс присутствия — только с момента старта
        try {
          await this.updateOperationStatus(this.taskData.id, 'work');
        } catch (e) {
          // Сервер операцию не принял (сообщение уже показано) — возвращаем экран в исходное
          // состояние и снимаем бронь, иначе стол остаётся занятым под неначатую операцию
          this.isOperationStarted = false;
          this.stopPresence();
          this.releaseCurrentLock();
          await this.fetchTaskData();
          return;
        }
        await this.setStepStatus(this.currentStep.id, 'work');
        // Обновляем данные задания — появляется batch_product_id (доступно изменение серийника),
        // затем возвращаем состояние «операция начата» (fetchTaskData его сбрасывает)
        await this.fetchTaskData();
        this.isOperationStarted = true;
        this.startPresence();
      }
    },

    // Логика кнопки "Продолжить операцию": возобновляем трекинг, НЕ сбрасывая
    // fact_start_datetime операции (реальная выработка по времени сохраняется)
    async continueOperation() {
      if (this.isOperationStarted) return;
      // Бронируем; если занято другим — не продолжаем
      if (!await this.acquireLock()) return;
      this.isOperationStarted = true;
      this.startPresence(); // пульс присутствия — только с момента продолжения
      if (this.taskData) {
        this.taskData.operation_status = 'work';
      }
      // Текущий (первый незавершённый) шаг помечаем в работу
      if (this.currentStep && this.currentStep.status !== 'done') {
        await this.setStepStatus(this.currentStep.id, 'work');
      }
    },

    // Логика кнопки "Следующий шаг"
    async goToNextStep() {
      if (this.currentStepIndex < this.taskData.steps.length - 1) {
        // Завершаем только не пройденный ранее шаг — пройденный (done) не перезаписываем
        if (this.currentStep.status !== 'done') {
          await this.setStepStatus(this.currentStep.id, 'done');
        }
        this.currentStepIndex++;
        // В работу переводим только ещё не завершённый шаг (иначе это просмотр пройденного)
        if (this.currentStep.status !== 'done') {
          await this.setStepStatus(this.currentStep.id, 'work');
        }
      }
    },

    // Логика кнопки "Вернуться назад" — только просмотр пройденных шагов, статусы не трогаем
    async goToPreviousStep() {
      if (this.currentStepIndex > 0) {
        if (this.hasPendingPickingItems) {
          if (!confirm('Есть несохранённые позиции комплектования. Продолжить без сохранения?')) return;
        }
        this.currentStepIndex--;
      }
    },

    // Открыть подтверждение перемещения изделия в карантин
    openQuarantineModal() {
      this.quarantineDescription = '';
      this.quarantineError = '';
      this.showQuarantineModal = true;
    },

    // Закрыть подтверждение (во время отправки закрывать нельзя)
    closeQuarantineModal() {
      if (this.quarantineSaving) return;
      this.showQuarantineModal = false;
      this.quarantineDescription = '';
      this.quarantineError = '';
    },

    // Карантин на рабочем месте ОТК: изделие останавливается и уходит на склад карантина
    async quarantineFromOtk() {
      if (this.quarantineSaving) return;

      const description = (this.quarantineDescription || '').trim();
      // Те же правила, что в Form Request: причина обязательна (Z-05), не длиннее 2000 символов
      if (description === '') {
        this.quarantineError = 'Опишите несоответствие — без описания зафиксировать его нельзя.';
        return;
      }
      if (description.length > 2000) {
        this.quarantineError = 'Описание несоответствия длиннее 2000 символов.';
        return;
      }

      this.quarantineSaving = true;
      this.quarantineError = '';
      try {
        const { data } = await axios.post('/api/productionOperation/quarantineFromOtk', {
          operation_id: this.taskData.id,
          description: description || null,
        });
        if (data?.success) {
          this.showQuarantineModal = false;
          this.quarantineDescription = '';
          // Расследование могло не завестись из-за пустой настройки ответственного:
          // карантин это не отменяет, но пользователю говорим прямо (Д-22, Z-04)
          const message = data.warning
            ? (data.message || 'Изделие остановлено и отправлено на склад карантина') + '\n\n' + data.warning
            : (data.message || 'Изделие остановлено и отправлено на склад карантина');
          alert(message);
          this.fetchTaskData();
        } else {
          // 200 с success=false — текст сервера показываем как есть
          this.quarantineError = data?.message || 'Не удалось выполнить операцию';
        }
      } catch (e) {
        this.quarantineError = this.extractErrorMessage(e, 'Не удалось выполнить операцию');
      } finally {
        this.quarantineSaving = false;
      }
    },

    // Достаём человекочитаемый текст ошибки: сначала подробности валидации, затем общее сообщение
    extractErrorMessage(error, fallback) {
      const payload = error?.response?.data;
      const errors = payload?.errors;
      if (errors && typeof errors === 'object') {
        const messages = Object.values(errors)
          .flat()
          .filter(Boolean);
        if (messages.length) return messages.join('\n');
      }
      return payload?.message || fallback;
    },

    // Открыть режим редактирования серийного номера
    startSerialEdit() {
      this.serialDraft = this.taskData?.serial_number || '';
      this.isSerialEditing = true;
      this.$nextTick(() => {
        this.$refs.serialInput?.focus();
      });
    },

    // Выйти из режима редактирования без сохранения
    cancelSerialEdit() {
      if (this.serialSaving) return;
      this.serialDraft = this.taskData?.serial_number || '';
      this.isSerialEditing = false;
    },

    // Сохранение серийного номера изделия прямо на рабочем месте
    async saveSerial() {
      const bpId = this.taskData?.batch_product_id;
      if (!bpId) return;
      const value = (this.serialDraft || '').trim();
      if (value === (this.taskData?.serial_number || '')) {
        // Ничего не поменялось — просто закрываем режим редактирования
        this.isSerialEditing = false;
        return;
      }

      this.serialSaving = true;
      try {
        const { data } = await axios.post('/api/batchProduct/updateSerialNumber', {
          id: bpId,
          serial_number: value,
        });
        if (data?.success) {
          this.taskData.serial_number = value;
          this.isSerialEditing = false;
        } else {
          alert(data?.message || 'Не удалось сохранить серийный номер');
        }
      } catch (e) {
        alert(e?.response?.data?.message || 'Не удалось сохранить серийный номер');
      } finally {
        this.serialSaving = false;
      }
    },

    // Логика кнопки "Завершить"
    async completeOperation(force = false) {
      if (this.currentStepIndex === this.taskData.steps.length - 1 || !this.taskData.steps.length) {
        // Сначала завершаем операцию, и только потом закрываем последний шаг:
        // при отказе сервера шаг остаётся открытым и работу можно продолжить
        await this.updateOperationStatus(this.taskData.id, 'done', force);

        if (this.currentStep?.id) {
          await this.setStepStatus(this.currentStep.id, 'done');
        }

        // Если это операция комплектования с отсканированными позициями - используем API с автоматическим списанием
        if (this.isPickingDeskModule) {

          const pickedProducts = this.pickedItemsData.map(instruction => {
            return instruction.picked_items ? instruction.picked_items.map(item => {
              return {
                storehouse_product_id: item.storehouse_product_id
              }
            }) : []
          }).flat().filter(item => item.storehouse_product_id)

          try {
            const response = await axios.post('/api/tsdScan/completePickingOperation', {
              operation_id: this.taskData.id,
              picked_products: pickedProducts,
              module_id: this.currentModule.id,
            });
            // console.log(response.data)
          } catch (error) {
            console.error('Ошибка при завершении операции комплектования:', error);
          }
        }

        this.stopCamera();
        this.stopPresence(); // AND-193: гасим пульс присутствия завершённой операции
        this.releaseCurrentLock(); // AND-193: снимаем лок операции и стола (до сброса taskData)
        await this.resetOperationState(); // Сбрасываем состояние
        this.fetchTaskData(); // Обновляем данные с сервера
      }
    },

    // Сброс состояния операции
    resetOperationState() {
      this.isOperationStarted = false;
      this.currentStepIndex = 0;
      this.selectedOperationId = null; // после завершения — авто-подбор следующей
      this.photoGalleryByStep = {};
      this.showPhotoGallery = false;
      this.taskData.steps.forEach((step) => {
        step.status = 'pending'; // Сбрасываем статус всех шагов
      });
      this.stopCamera();
    },

    // Отправка запроса на обновление статуса операции
    async updateOperationStatus(operationId, status, forceComplete = false) {
      try {
        await axios.post('/api/productionOperation/changeStatus', {
          operation_id: operationId,
          status: status,
          force_complete: forceComplete || undefined,
        });
      } catch (error) {
        const serverMessage = error.response?.data?.message;
        if (serverMessage && serverMessage.includes('Распределите все материалы')) {
          // Сервер перечисляет, каких позиций не хватает — показываем текст как есть
          this.packingWarningMessage = serverMessage;
          this.showPackingWarning = true;
        } else if (serverMessage && serverMessage.includes('Возьмите все комплектующие')) {
          // Сервер перечисляет, чего не хватает в комплекте — показываем текст как есть
          this.pickingWarningMessage = serverMessage;
          this.showPickingWarning = true;
        } else {
          alert(serverMessage || 'Произошла ошибка, попробуйте ещё раз');
        }
        console.error('Ошибка при обновлении статуса операции:', error);
        throw error;
      }
    },
    startOperationModal() {
      this.isModalVisible = true;
      this.modalTitle = 'начать операцию';
    },
    endOperationModal() {
      this.isModalVisible = true;
      this.modalTitle = 'завершить операцию';
    },
    nextOperationModal() {
      this.isModalVisible = true;
      this.modalTitle = 'перейти к следующему шагу';
    },
    closeModal() {
      this.isModalVisible = false;
    },

    handlePickingCompleted() {
      // Обработка завершения комплектования
      console.log('Комплектование завершено');
      // Можно обновить данные или перейти к следующему шагу
      this.fetchTaskData();
    },
    handlePickedItemsData(pickedData) {
      // Сохраняем данные отсканированных позиций для списания
      this.pickedItemsData = pickedData;
      console.log('Отсканированные позиции:', pickedData);
    },
    handleHasPending(hasPending) {
      this.hasPendingPickingItems = hasPending;
    },
    async savePickingProgress() {
      const pickingRef = this.$refs.pickingComponents;
      if (!pickingRef) {
        // Блок комплектования не на экране — сохранять нечем, оператор должен об этом знать
        const text = 'Вернитесь на шаг комплектования, чтобы сохранить отмеченные позиции';
        if (this.$root.$toast) {
          this.$root.$toast.error(text);
        } else {
          alert(text);
        }
        return false;
      }
      this.isSavingProgress = true;
      try {
        return await pickingRef.savePendingProgress();
      } finally {
        this.isSavingProgress = false;
      }
    },
    handleBeforeUnload(e) {
      // AND-193: снять лок операции и стола при перезагрузке/закрытии вкладки.
      // fetch с keepalive доставляется даже при выгрузке страницы и, в отличие от sendBeacon,
      // умеет ставить заголовки — передаём X-XSRF-TOKEN, иначе Sanctum отклонит запрос (CSRF).
      // Лок держится только пока операция в работе — иначе освобождать нечего.
      if (this.isOperationStarted && this.taskData?.id) {
        const token = this.getXsrfToken();
        fetch('/api/productionOperation/releaseLock', {
          method: 'POST',
          credentials: 'include',
          keepalive: true,
          headers: {
            'Content-Type': 'application/json',
            'X-Requested-With': 'XMLHttpRequest',
            ...(token ? { 'X-XSRF-TOKEN': token } : {}),
          },
          body: JSON.stringify({ operation_id: this.taskData.id, module_id: this.$route.params.id }),
        }).catch(() => {});
      }
      if (this.hasPendingPickingItems) {
        e.preventDefault();
        e.returnValue = '';
      }
    },

    // Достаёт CSRF-токен из cookie (XSRF-TOKEN), декодируя как это делает axios
    getXsrfToken() {
      const match = document.cookie.match(/(?:^|;\s*)XSRF-TOKEN=([^;]+)/);
      return match ? decodeURIComponent(match[1]) : null;
    },

    // Обогащаем компоненты данными о взятых позициях для печати
    enrichComponentsWithPickedItems(components) {
      if (!components || !this.pickedItemsData || this.pickedItemsData.length === 0) {
        return components;
      }

      return components.map(component => {
        // Ищем соответствующие picked_items для этого компонента
        const matchingPickedInstruction = this.pickedItemsData.find(
          instruction => instruction.product_id === component.product_id
        );

        if (matchingPickedInstruction && matchingPickedInstruction.picked_items) {
          // Добавляем picked_items к компоненту
          return {
            ...component,
            picked_items: matchingPickedInstruction.picked_items
          };
        }

        return component;
      });
    },
    async confirmModal() {
      this.closeModal()
      if (this.modalTitle === 'начать операцию') {
        this.startOperation()
      } else if (this.modalTitle === 'завершить операцию') {
        // Сначала сохраняем прогресс, потом завершаем
        if (this.hasPendingPickingItems) {
          const saved = await this.savePickingProgress();
          if (!saved) return;
        }
        // Проверка распределения упаковочных мест через API
        if (this.hasPackagingStep) {
          try {
            const res = await axios.post('/api/packingPlace/getOrderMaterials', {
              production_task_id: this.taskData.production_task_id,
            });
            const materials = res.data.data || [];
            const notDistributed = materials.filter(m => m.distributed_quantity < m.required_quantity);
            if (notDistributed.length > 0) {
              this.showPackingWarning = true;
              return;
            }
          } catch (e) {
            console.error('Ошибка проверки упаковочных мест:', e);
            alert('Ошибка проверки упаковочных мест');
            return;
          }
        }
        // Отказ сервера уже показан оператору в updateOperationStatus — здесь гасим его,
        // чтобы он не всплывал необработанным промисом
        this.completeOperation().catch(() => {})
      } else if (this.modalTitle === 'перейти к следующему шагу') {
        if (this.hasPendingPickingItems) {
          if (!confirm('Есть несохранённые позиции комплектования. Продолжить без сохранения?')) return;
        }
        this.goToNextStep()
      }
    },

    // Локальное обновление статуса шага
    updateLocalStepStatus(stepId, status) {
      const step = this.taskData.steps.find((step) => step.id === stepId);
      if (step) {
        step.status = status;
      }
    },

    // Прикрепление документа к шагу
    triggerStepDocumentUpload() {
      if (this.isUploadingDocument) return;
      this.$refs.stepDocumentInput?.click();
    },

    async handleStepDocumentSelect(event) {
      const file = event.target.files?.[0];
      event.target.value = '';
      if (!file) return;

      const stepId = this.currentStep?.operation_step?.id;
      if (!this.productionTaskOperationId) {
        alert('Не найдена операция для прикрепления документа');
        return;
      }

      this.isUploadingDocument = true;
      try {
        const formData = new FormData();
        formData.append('file', file);
        formData.append('production_task_operation_id', this.productionTaskOperationId);
        if (stepId) formData.append('operation_step_id', stepId);

        const { data } = await axios.post('/api/productionOperation/uploadStepDocument', formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });

        if (data.success) {
          this.stepDocuments.push({
            stepId,
            file_id: data.document.file_id,
            name: data.document.name,
            preview_url: data.document.preview_url,
          });
        } else {
          alert(data.message || 'Не удалось загрузить документ');
        }
      } catch (error) {
        console.error('Ошибка загрузки документа:', error);
        alert(error.response?.data?.message || 'Ошибка загрузки документа');
      } finally {
        this.isUploadingDocument = false;
      }
    },

    // Открыть окно предпросмотра камеры
    openCameraPreview() {
      if (!this.cameraReady || this.isPhotoTaking || !this.isOperationStarted) return;
      this.clearCapturedPhoto();
      this.showCameraPreview = true;
      // Привязываем поток к видео предпросмотра
      this.$nextTick(() => {
        if (this.$refs.previewVideo && this.videoStream) {
          this.$refs.previewVideo.srcObject = this.videoStream;
        }
      });
    },

    // Зафиксировать кадр (предпросмотр перед сохранением)
    async capturePreviewPhoto() {
      if (!this.cameraReady) return;
      const blob = await this.takePhoto();
      if (!blob) {
        console.error('Не удалось сделать снимок.');
        return;
      }
      this.clearCapturedPhotoUrl();
      this.capturedPhotoBlob = blob;
      this.capturedPhotoUrl = URL.createObjectURL(blob);
    },

    // Переснять — вернуться к живому предпросмотру
    retakePhoto() {
      this.clearCapturedPhoto();
      this.$nextTick(() => {
        if (this.$refs.previewVideo && this.videoStream) {
          this.$refs.previewVideo.srcObject = this.videoStream;
        }
      });
    },

    // Подтвердить и загрузить зафиксированный кадр
    async confirmCapturedPhoto() {
      if (!this.capturedPhotoBlob || this.isSavingPhoto) return;
      this.isSavingPhoto = true;
      this.isPhotoTaking = true;
      try {
        const result = await this.uploadPhoto(this.capturedPhotoBlob);
        if (result) {
          this.photoFlash = true;
          setTimeout(() => { this.photoFlash = false; }, 300);
          const key = result.operationStepId ?? this.currentStepKey;
          this.addPhotoToStep(key, { url: '/' + result.path, photoId: result.photoId });
          this.showPhotoGallery = true;
        }
      } finally {
        this.isSavingPhoto = false;
        this.isPhotoTaking = false;
        this.closeCameraPreview();
      }
    },

    // Закрыть окно предпросмотра
    closeCameraPreview() {
      this.showCameraPreview = false;
      this.clearCapturedPhoto();
    },

    clearCapturedPhotoUrl() {
      if (this.capturedPhotoUrl) {
        URL.revokeObjectURL(this.capturedPhotoUrl);
        this.capturedPhotoUrl = null;
      }
    },

    clearCapturedPhoto() {
      this.clearCapturedPhotoUrl();
      this.capturedPhotoBlob = null;
    },

    // Обработчик нажатия клавиш
    onKeyDown(event) {
      // Настройка клавиши в модалке
      if (this.showKeybindModal) {
        event.preventDefault();
        if (event.code === 'Escape') {
          this.closeKeybindModal();
          return;
        }
        this.photoHotkeyCode = event.code;
        localStorage.setItem('photo_hotkey', event.code);
        this.showKeybindModal = false;
        return;
      }

      // Закрытие окна предпросмотра камеры по Escape
      if (this.showCameraPreview && event.code === 'Escape') {
        event.preventDefault();
        this.closeCameraPreview();
        return;
      }

      // Модалка карантина: Escape закрывает её, горячая клавиша фото не срабатывает
      if (this.showQuarantineModal) {
        if (event.code === 'Escape') {
          event.preventDefault();
          this.closeQuarantineModal();
        }
        return;
      }

      // Не срабатывать при вводе текста
      const tag = document.activeElement?.tagName?.toLowerCase();
      if (tag === 'input' || tag === 'textarea' || tag === 'select') return;

      // Не срабатывать при открытой модалке (кроме окна предпросмотра камеры)
      if (this.isModalVisible) return;

      // Игнорировать зажатие клавиши
      if (event.repeat) return;

      // Проверка совпадения с горячей клавишей
      if (event.code === this.photoHotkeyCode) {
        event.preventDefault();
        // Машина состояний окна предпросмотра:
        // 1-е нажатие — открыть, 2-е — зафиксировать кадр, 3-е — сохранить
        if (!this.showCameraPreview) {
          this.openCameraPreview();
        } else if (!this.capturedPhotoUrl) {
          this.capturePreviewPhoto();
        } else {
          this.confirmCapturedPhoto();
        }
      }
    },

    // Добавить фото в галерею конкретного шага (реактивно)
    addPhotoToStep(stepKey, photo) {
      if (stepKey == null) return;
      const list = this.photoGalleryByStep[stepKey] ? [...this.photoGalleryByStep[stepKey]] : [];
      list.push(photo);
      this.photoGalleryByStep = { ...this.photoGalleryByStep, [stepKey]: list };
    },

    async removePhoto(index) {
      const key = this.currentStepKey;
      const gallery = this.currentStepGallery;
      const photo = gallery[index];
      if (!photo) return;
      try {
        await axios.post('/api/productionOperation/deleteOperationPhoto', {
          production_task_operation_id: this.productionTaskOperationId,
          photo_id: photo.photoId,
        });
        const list = [...gallery];
        list.splice(index, 1);
        this.photoGalleryByStep = { ...this.photoGalleryByStep, [key]: list };
        if (list.length === 0) {
          this.showPhotoGallery = false;
        }
      } catch (err) {
        console.error('Ошибка удаления фото:', err);
      }
    },

    openKeybindModal() {
      this.showKeybindModal = true;
    },

    closeKeybindModal() {
      this.showKeybindModal = false;
    },

    resetHotkey() {
      this.photoHotkeyCode = 'Space';
      localStorage.setItem('photo_hotkey', 'Space');
      this.showKeybindModal = false;
    },

    goToPackingPlaces() {
      this.activeView = 'packing';
    },

    getHotkeyLabel(code) {
      const labels = {
        'Space': 'Пробел',
        'Enter': 'Enter',
        'NumpadEnter': 'Num Enter',
        'ShiftLeft': 'L Shift',
        'ShiftRight': 'R Shift',
        'ControlLeft': 'L Ctrl',
        'ControlRight': 'R Ctrl',
        'AltLeft': 'L Alt',
        'AltRight': 'R Alt',
      };
      if (labels[code]) return labels[code];
      if (code.startsWith('Key')) return code.replace('Key', '');
      if (code.startsWith('Digit')) return code.replace('Digit', '');
      if (code.startsWith('Numpad')) return 'Num ' + code.replace('Numpad', '');
      if (code.startsWith('F') && !isNaN(code.slice(1))) return code;
      return code;
    },

  },
};
</script>

<style scoped>
@keyframes borderFlash {
  0% { opacity: 1; }
  100% { opacity: 0; }
}
</style>
